'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeleteThread } from '@/api/hooks/useDeleteThread'
import type { ThreadType } from '@/api/hooks/useThreads'
import { threadDeleteToast } from '@/components/molecules/custom-async-toasts'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { useIndividuals } from '@/hooks/useIndividuals'
import { DateUtils } from '@/lib/utils'

import { ConfirmDialog } from '../confirm-dialog'
import type { CustomColumnDef } from '../data-table'
import { ColumnActions } from '../data-table/actions-column'

export const useThreadsTableColumns: (
  threads: ThreadType[],
) => CustomColumnDef<ThreadType>[] = () => {
  const { selectedIndividualId } = useIndividuals()
  const deleteThread = useDeleteThread()
  const { t } = useTranslation()

  const onRequestDeleteThread = (id: string) => {
    if (!selectedIndividualId) return
    const deletionPromise = deleteThread.mutateAsync({
      individualId: selectedIndividualId,
      threadId: id,
    })
    threadDeleteToast(deletionPromise)
  }

  return [
    {
      cell: ({
        row: {
          original: { summary, title, updatedAt },
        },
      }) => {
        return (
          <div>
            <div className="flex flex-col gap-1">
              {/* AI-generated. The backend falls back to the first user message /
                  last assistant reply for conversations without a generated title,
                  hence line-clamp-1 — the fallback can be a whole paragraph. */}
              <p className="font-semibold line-clamp-1 max-w-prose">
                {title || t('conversationsTable.untitled')}
              </p>
              <p className="line-clamp-2 text-text-secondary max-w-prose">{summary}</p>
            </div>
            <div className="@3xl/table:hidden mt-2 text-xs text-text-secondary whitespace-nowrap font-medium">
              {DateUtils.formatRelativeDate(new Date(updatedAt), t)}
            </div>
          </div>
        )
      },
      header: t('conversationsTable.columns.conversation'),
      id: 'messageSummary',
      size: Number.POSITIVE_INFINITY,
    },
    {
      accessorKey: 'updatedAt',
      cell: ({ cell }) => {
        const date = cell.getValue<string>() ? new Date(cell.getValue<string>()) : new Date()

        return (
          <span className="text-text-secondary text-right whitespace-nowrap block">
            {DateUtils.formatRelativeDate(date, t)}
          </span>
        )
      },
      columnClassName: 'hidden @3xl/table:table-cell',
      header: () => <div className="text-right">{t('global.updatedAt')}</div>,
      size: 120,
    },
    {
      cell: ({ row }) => {
        const [deleteOpen, setDeleteOpen] = useState(false)

        return (
          <ColumnActions
            dropDownMenuContent={
              <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
                {t('conversationsTable.rowActionsDropdown.deleteConversation')}
              </DropdownMenuItem>
            }
          >
            <ConfirmDialog
              onConfirm={() => onRequestDeleteThread(row.original.id)}
              onOpenChange={setDeleteOpen}
              open={deleteOpen}
              variant="destructive"
            />
          </ColumnActions>
        )
      },
      columnClassName: 'w-px p-2 @3xl/table:p-5',
      enableHiding: false,
      id: 'actions',
    },
  ] satisfies CustomColumnDef<ThreadType>[]
}
